/**
 * Admin -> New template form (Phase 30).
 *
 * Creates a new template directly in DDB (no PR flow).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAdminTemplate } from "../services/adminTemplates.js";
import { listAdminAgents } from "../services/adminAgents.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

const VALID_FORMATS = ["xlsx", "pptx"];
const VALID_PARSER_TYPES = [
  "account_plan",
  "value_pyramid",
  "three_year",
  "crazy4",
];

export default function AdminTemplateNew() {
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    templateId: "",
    name: "",
    format: "xlsx",
    s3Key: "",
    applicableAgents: [],
    parserType: "",
    enabled: true,
  });

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listAdminAgents();
      setAgents(resp.agents || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  function toggleAgent(agentId) {
    setForm((f) => ({
      ...f,
      applicableAgents: f.applicableAgents.includes(agentId)
        ? f.applicableAgents.filter((a) => a !== agentId)
        : [...f.applicableAgents, agentId],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await createAdminTemplate(form);
      addToast({ type: "success", message: "Template created." });
      navigate("/admin/templates");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSkeleton variant="line" />;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>New Template</h1>
      </div>

      {error && <ErrorBubble message={error} />}

      <form onSubmit={handleSubmit} className="admin-form">
        <label className="admin-form__field">
          <span>Template ID</span>
          <input
            type="text"
            value={form.templateId}
            onChange={(e) =>
              setForm((f) => ({ ...f, templateId: e.target.value }))
            }
            pattern="[a-z0-9][a-z0-9_-]*"
            required
            placeholder="e.g. rtr, vp, 3y"
          />
        </label>

        <label className="admin-form__field">
          <span>Name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            placeholder="e.g. Account Plan (RTR)"
          />
        </label>

        <label className="admin-form__field">
          <span>Format</span>
          <select
            value={form.format}
            onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
          >
            {VALID_FORMATS.map((fmt) => (
              <option key={fmt} value={fmt}>
                {fmt.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        <label className="admin-form__field">
          <span>Parser Type</span>
          <select
            value={form.parserType}
            onChange={(e) =>
              setForm((f) => ({ ...f, parserType: e.target.value }))
            }
          >
            <option value="">-- select --</option>
            {VALID_PARSER_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>

        <label className="admin-form__field">
          <span>S3 Key</span>
          <input
            type="text"
            value={form.s3Key}
            onChange={(e) => setForm((f) => ({ ...f, s3Key: e.target.value }))}
            placeholder="templates/filename.xlsx"
          />
        </label>

        <fieldset className="admin-form__fieldset">
          <legend>Applicable Agents</legend>
          {agents.length === 0 ? (
            <p className="admin-muted">No agents available.</p>
          ) : (
            agents.map((a) => (
              <label key={a.agentId} className="admin-form__checkbox">
                <input
                  type="checkbox"
                  checked={form.applicableAgents.includes(a.agentId)}
                  onChange={() => toggleAgent(a.agentId)}
                />
                {a.name} <code>{a.agentId}</code>
              </label>
            ))
          )}
        </fieldset>

        <label className="admin-form__field admin-form__field--inline">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) =>
              setForm((f) => ({ ...f, enabled: e.target.checked }))
            }
          />
          <span>Enabled</span>
        </label>

        <div className="admin-form__actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? "Creating\u2026" : "Create template"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin/templates")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
